import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProperty } from '../../context/PropertyContext';

const PropertyList = ({ properties, onEdit, onDelete }) => {
  const { formatPrice } = useProperty();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [sortBy, setSortBy] = useState('newest');

  const filteredProperties = properties
    .filter(property => {
      const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           property.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'All' || property.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.id - a.id;
        case 'oldest':
          return a.id - b.id;
        case 'price-high':
          return b.price - a.price;
        case 'price-low':
          return a.price - b.price;
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

  const propertyTypes = ['All', ...new Set(properties.map(p => p.type))];

  return (
    <div className="property-list">
      <div className="list-controls">
        <div className="search-bar">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            {propertyTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
            <option value="title">Title A-Z</option>
          </select>
        </div>
      </div>

      <div className="list-header">
        <span>Found {filteredProperties.length} properties</span>
      </div>

      {filteredProperties.length > 0 ? (
        <div className="properties-table">
          <div className="table-header">
            <div className="col-image">Image</div>
            <div className="col-details">Property Details</div>
            <div className="col-price">Price</div>
            <div className="col-status">Status</div>
            <div className="col-actions">Actions</div>
          </div>
          
          <div className="table-body">
            {filteredProperties.map((property, index) => (
              <motion.div
                key={property.id}
                className="table-row"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
              >
                <div className="col-image">
                  <img src={property.images[0]} alt={property.title} />
                </div>
                
                <div className="col-details">
                  <h4>{property.title}</h4>
                  <p className="location">
                    <i className="fas fa-map-marker-alt"></i>
                    {property.location}
                  </p>
                  <div className="property-features">
                    <span><i className="fas fa-bed"></i> {property.bedrooms}</span>
                    <span><i className="fas fa-bath"></i> {property.bathrooms}</span>
                    <span><i className="fas fa-ruler-combined"></i> {property.area.toLocaleString()} sq ft</span>
                  </div>
                  <span className="property-type">{property.type}</span>
                </div>
                
                <div className="col-price">
                  <span className="price">{formatPrice(property.price)}</span>
                </div>
                
                <div className="col-status">
                  <span className={`status ${property.status.toLowerCase().replace(' ', '-')}`}>
                    {property.status}
                  </span>
                </div>
                
                <div className="col-actions">
                  <button
                    className="action-btn edit"
                    onClick={() => onEdit(property)}
                    title="Edit Property"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    className="action-btn delete"
                    onClick={() => onDelete(property.id)}
                    title="Delete Property"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <i className="fas fa-search"></i>
          <h3>No Properties Found</h3>
          <p>Try adjusting your search criteria or add a new property.</p>
        </div>
      )}
    </div>
  );
};

export default PropertyList;